/**
 * Two-Factor Authentication Step Component
 * 
 * Handles 2FA code input and verification using Better Auth.
 * Memoized for performance optimization.
 */

import { memo, useEffect, useRef, useState } from "react";

import { ShieldCheck, Shield, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";

import type { AuthState, AuthAction } from "@/types/state";

interface TwoFactorStepProps {
  state: AuthState;
  dispatch: (action: AuthAction) => void;
  onSuccess: () => void;
  onBack: () => void;
}

const TwoFactorStepComponent = ({ state, dispatch, onSuccess, onBack }: TwoFactorStepProps) => {
  const autoSubmitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);

  const handleVerify = async () => {
    if (state.totpCode.length !== 6) return;
    
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const { data, error } = await authClient.twoFactor.verifyTotp({
        code: state.totpCode,
        trustDevice: state.rememberDevice,
      });

      if (error) {
        throw error;
      }

      toast.success("Verification successful");
      onSuccess();
    } catch (error: any) {
      console.error("2FA Verification error:", error);
      toast.error(error.message || "Invalid verification code");
      dispatch({ type: 'SET_LOADING', payload: false });
      setIsAutoSubmitting(false);
    }
  };

  // Auto-submit when 6 digits are entered (debounced)
  useEffect(() => {
    if (state.totpCode.length === 6 && !state.loading && state.step !== 'submitting') {
      console.log('[TwoFactorStep] TOTP code complete, scheduling auto-submit');
      
      // Clear any existing timeout
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }

      // Show visual feedback immediately
      setIsAutoSubmitting(true);

      // Debounce for 300ms to allow user to see the complete code
      autoSubmitTimeoutRef.current = setTimeout(() => {
        console.log('[TwoFactorStep] Auto-submitting TOTP code');
        handleVerify();
      }, 300);
    } else {
      setIsAutoSubmitting(false);
    }

    // Cleanup timeout on unmount or when code changes
    return () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current);
      }
    };
  }, [state.totpCode, state.loading, state.step, handleVerify]); // Added handleVerify to dependencies

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (state.totpCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center space-y-2">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold">Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="totp">Verification Code</Label>
        <div className="flex flex-col items-center gap-2">
          <div className={`transition-all duration-300 ${
            isAutoSubmitting ? 'scale-105 ring-2 ring-primary/50 rounded-lg' : ''
          }`}>
            <InputOTP
              maxLength={6}
              value={state.totpCode}
              onChange={(value) => dispatch({ type: 'SET_TOTP_CODE', payload: value })}
              disabled={state.loading}
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
          {isAutoSubmitting && (
            <div className="flex items-center gap-2 text-primary text-sm animate-fade-in">
              <CheckCircle2 className="h-4 w-4 animate-scale-in" />
              <span>Verifying code...</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start space-x-2 p-3 rounded-lg bg-muted/50 border border-border/50">
        <Checkbox
          id="remember-device"
          checked={state.rememberDevice}
          onCheckedChange={(checked) => 
            dispatch({ type: 'SET_REMEMBER_DEVICE', payload: checked === true })
          }
          disabled={state.loading}
        />
        <div className="space-y-1 leading-none">
          <Label
            htmlFor="remember-device"
            className="text-sm font-medium cursor-pointer"
          >
            Remember this device for 30 days
          </Label>
          <p className="text-xs text-muted-foreground">
            <Shield className="inline h-3 w-3 mr-1" />
            You won't need to enter 2FA codes on this device for the next month
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={state.loading}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          type="submit"
          disabled={state.totpCode.length !== 6 || state.loading}
          className="flex-1"
        >
          {state.loading ? 'Verifying...' : 'Verify'}
        </Button>
      </div>
    </form>
  );
};

export const TwoFactorStep = memo(TwoFactorStepComponent);
